// ============================================================================
// Service données brutes GRDF (DEV-595) — appelle la route dapi `/datav2/series`
// et assemble les 2 vues de la page dédiée PCE : Facturation (periode/publié) &
// Suivi quotidien (daily/informatif). 100 % brut (pas de coût/calculé).
// ----------------------------------------------------------------------------
// Logique portée 1:1 depuis le générateur de fixtures validé (stories RÉEL).
// ============================================================================
import axios from "@core/plugins/axios.plugin"
import type { ProvenanceCode } from "@modules/data/components/data/quality/dataQuality"
import type { RawReading } from "@modules/data/components/data/table/DataRawReadingsTable.vue"
import type { RawSeries } from "@modules/data/components/data/chart/RawSeriesChart.vue"
import type {
    DataSourceView,
    ChartGroup,
} from "@modules/data/components/data/grdf/SourceDataGrdfView.vue"

/** Point x : date simple (daily/point) ou paire [from,to] (periode). */
type Datav2X = string | [string, string]

/** Une série colonnaire renvoyée par la route dapi. */
type Datav2Series = {
    _id_source: string
    variable: string
    freq: "daily" | "periode" | "point"
    stat: string
    x: Datav2X[]
    v: Array<number | null>
    t: ProvenanceCode[]
}

/** Réponse de `POST /datav2/series`. */
type Datav2Response = {
    meta: { provenance: string[]; units: Record<string, string> }
    data: Datav2Series[]
    periode: { data: unknown[] }
}

/** Libellés FR par variable (groupes de graphes + séries). */
const LABEL: Record<string, string> = {
    energie_gaz_tout: "Énergie",
    volume_gaz_tout: "Volume brut",
    volumeconverti_gaz_tout: "Volume converti",
    idxvolume_gaz_tout: "Index volume",
    volumeenergie_gaz_coef: "Coeff. conversion",
}

/** Colonnes numériques du relevé (toutes `number | null`). */
type NumericReadingField = "energie" | "volumeBrut" | "volumeConverti" | "coeff"

/** Mapping variable → colonne (numérique) du tableau de relevés. */
const READING_FIELD: Record<string, NumericReadingField> = {
    energie_gaz_tout: "energie",
    volume_gaz_tout: "volumeBrut",
    volumeconverti_gaz_tout: "volumeConverti",
    volumeenergie_gaz_coef: "coeff",
}

/** Variables « chaîne de mesure » (groupes + tableau ; hors index ponctuel). */
const CHAIN_VARS = [
    "energie_gaz_tout",
    "volume_gaz_tout",
    "volumeconverti_gaz_tout",
    "volumeenergie_gaz_coef",
]

/** 1 appel dapi `/datav2/series` (proxy front `/api/dapi`). */
async function fetchSeries(
    idSource: string,
    dateFrom: string,
    dateTo: string,
    variables: string[],
    freqs: string[],
    provenanceInclude?: string[]
): Promise<Datav2Response> {
    const { data } = await axios.post<Datav2Response>(
        `/api/dapi/datav2/series`,
        {
            _id_source: [idSource],
            date_from: dateFrom,
            date_to: dateTo,
            variables,
            freqs,
            ...(provenanceInclude
                ? { provenance_include: provenanceInclude }
                : {}),
        }
    )
    return data
}

/** Réponse dapi → séries prêtes pour `RawSeriesChart` (unité depuis meta). */
function toRawSeries(resp: Datav2Response): RawSeries[] {
    const units = resp.meta?.units ?? {}
    return (resp.data ?? []).map((s) => ({
        variable: s.variable,
        label: LABEL[s.variable] ?? s.variable,
        unit: units[s.variable] ?? "",
        freq: s.freq,
        x: s.x,
        v: s.v,
        t: s.t,
    }))
}

/** Groupes de graphes (1 par unité homogène) ; l'index (ponctuel) est partagé. */
function toChartGroups(
    chainResp: Datav2Response,
    idxSeries: RawSeries[]
): ChartGroup[] {
    const series = toRawSeries(chainResp)
    const pick = (v: string) => series.filter((s) => s.variable === v)
    return (
        [
            { title: "Énergie", series: pick("energie_gaz_tout") },
            {
                title: "Volumes",
                series: [
                    ...pick("volume_gaz_tout"),
                    ...pick("volumeconverti_gaz_tout"),
                ],
            },
            { title: "Index volume", series: idxSeries },
            {
                title: "Coeff. conversion",
                series: pick("volumeenergie_gaz_coef"),
            },
        ] as ChartGroup[]
    ).filter((g) => g.series.length > 0)
}

/** Tableau de relevés : aligne les variables de la chaîne sur leur x + index. */
function toReadings(
    chainResp: Datav2Response,
    idxResp: Datav2Response
): RawReading[] {
    const by = new Map<string, RawReading>()
    for (const s of chainResp.data ?? []) {
        const field = READING_FIELD[s.variable]
        if (!field) continue
        s.x.forEach((x, i) => {
            const isPeriod = Array.isArray(x)
            const dateFrom = isPeriod ? x[0] : (x as string)
            const dateTo = isPeriod ? x[1] : (x as string)
            const key = isPeriod ? `${x[0]}|${x[1]}` : (x as string)
            let r = by.get(key)
            if (!r) {
                r = {
                    id: dateTo,
                    dateFrom,
                    dateTo,
                    index: null,
                    volumeBrut: null,
                    coeff: null,
                    pcs: null,
                    volumeConverti: null,
                    energie: null,
                    t: s.t[i],
                }
                by.set(key, r)
            }
            r[field] = s.v[i]
            if (s.variable === "energie_gaz_tout") r.t = s.t[i]
        })
    }
    // Index = point FIN ; on rattache par jour (clé = date du relevé).
    const idxByDay = new Map<string, number | null>()
    for (const s of idxResp.data ?? []) {
        s.x.forEach((x, i) => {
            const day = (Array.isArray(x) ? x[1] : (x as string)).slice(0, 10)
            idxByDay.set(day, s.v[i])
        })
    }
    for (const r of by.values()) {
        r.index = idxByDay.get(r.dateTo.slice(0, 10)) ?? null
    }
    // Plus récent en premier.
    return Array.from(by.values()).sort((a, b) =>
        a.dateFrom < b.dateFrom ? 1 : -1
    )
}

/**
 * Charge les 2 vues de données brutes GRDF pour un PCE sur une fenêtre.
 *  • Facturation = grain `periode` + provenance `raw_publiee` (conso facturée)
 *  • Suivi quotidien = grain `daily` + provenance `raw_informative` (estimation)
 *  • Index volume = `point` (FIN, partagé entre les 2 vues)
 */
export async function fetchSourceDataGrdf(
    idSource: string,
    dateFrom: string,
    dateTo: string
): Promise<DataSourceView[]> {
    const [factResp, suiviResp, idxResp] = await Promise.all([
        fetchSeries(
            idSource,
            dateFrom,
            dateTo,
            CHAIN_VARS,
            ["periode"],
            ["raw_publiee"]
        ),
        fetchSeries(
            idSource,
            dateFrom,
            dateTo,
            CHAIN_VARS,
            ["daily"],
            ["raw_informative"]
        ),
        fetchSeries(
            idSource,
            dateFrom,
            dateTo,
            ["idxvolume_gaz_tout"],
            ["point"]
        ),
    ])
    const idxSeries = toRawSeries(idxResp)

    return [
        {
            key: "facturation",
            label: "Facturation (publié)",
            readings: toReadings(factResp, idxResp),
            chartGroups: toChartGroups(factResp, idxSeries),
        },
        {
            key: "suivi",
            label: "Suivi quotidien (informatif)",
            readings: toReadings(suiviResp, idxResp),
            chartGroups: toChartGroups(suiviResp, idxSeries),
        },
    ]
}
